import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useAtomValue } from 'jotai';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import { type ANNOTATION_SHAPE_STYLING_TYPE, SKELETON_TOOLS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { SkeletonAnnotationT, SkeletonDataT } from '../../../types';
import { ScalingBoundingBox } from '../scaling-bounding-box/scaling-bounding-box';
import Shape from '../shape/shape';
import { VERTEX_TYPE } from '../shape/vertex';
import { getStylesForStylingType } from '../utils';
import { type BoundingBoxT, calculateBoundingBox, isScaleVertexType, scaleSkeletonData } from '../utils/scaling-utils';
import { useShapeScaling } from '../utils/use-shape-scaling';
import { HUMAN_SKELETON_TEMPLATE, SKELETON_TEMPLATE_BOUNDING_BOX_PADDING } from './constants';
import { NodeLabels } from './node-labels';
import { useDrawSkeleton } from './use-draw-skeleton';
import { useUpdateSkeleton } from './use-update-skeleton';
type PropsT = {
  isDrawing?: boolean;
  annotation?: SkeletonAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
function getAnchorPointForVertex(boundingBox: BoundingBoxT, vertex: {
  type: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE];
}): [number, number] {
  const halfWidth = boundingBox.width / 2 - SKELETON_TEMPLATE_BOUNDING_BOX_PADDING;
  const halfHeight = boundingBox.height / 2 - SKELETON_TEMPLATE_BOUNDING_BOX_PADDING;
  switch (vertex.type) {
    case VERTEX_TYPE.SCALE_TOP_LEFT:
      return [boundingBox.centerX + halfWidth, boundingBox.centerY + halfHeight];
    case VERTEX_TYPE.SCALE_TOP_CENTER:
      return [boundingBox.centerX, boundingBox.centerY + halfHeight];
    case VERTEX_TYPE.SCALE_TOP_RIGHT:
      return [boundingBox.centerX - halfWidth, boundingBox.centerY + halfHeight];
    case VERTEX_TYPE.SCALE_CENTER_RIGHT:
      return [boundingBox.centerX - halfWidth, boundingBox.centerY];
    case VERTEX_TYPE.SCALE_BOTTOM_RIGHT:
      return [boundingBox.centerX - halfWidth, boundingBox.centerY - halfHeight];
    case VERTEX_TYPE.SCALE_BOTTOM_CENTER:
      return [boundingBox.centerX, boundingBox.centerY - halfHeight];
    case VERTEX_TYPE.SCALE_BOTTOM_LEFT:
      return [boundingBox.centerX + halfWidth, boundingBox.centerY - halfHeight];
    case VERTEX_TYPE.SCALE_CENTER_LEFT:
      return [boundingBox.centerX + halfWidth, boundingBox.centerY];
    default:
      return [0, 0];
  }
}
export const Skeleton = ({
  isDrawing,
  annotation,
  annotationIdx,
  stylingType,
  disableChanges
}: PropsT) => {
  const {
    getStageCenter,
    updateAnnotation
  } = useStage();
  const {
    editableAnnotationAtom,
    focusedAnnotationIdAtom
  } = useStageAtoms();
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const skeletonRef = useRef<SVGGElement | null>(null);
  const [drawingSkeleton, setDrawingSkeleton] = useState<SkeletonAnnotationT | null>(annotation ?? null);
  const [selectedVertexIdx, setSelectedVertexIdx] = useState<number | null>(null);
  const [hoveredVertexIdx, setHoveredVertexIdx] = useState<number | null>(null);
  const [labelCoords, setLabelCoords] = useState<[number, number] | null>(null);
  const [selectedScaleHandle, setSelectedScaleHandle] = useState<(typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE] | null>(null);
  const [boundingBox, setBoundingBox] = useState<BoundingBoxT | null>(null);
  const originalBoundingBoxRef = useRef<BoundingBoxT | null>(null);
  const anchorPointRef = useRef<[number, number] | null>(null);
  const isLocked = Boolean(annotation?.locked);
  const isFocused = focusedAnnotationId === annotationIdx;
  const isEditable = editableAnnotation === annotationIdx;
  const {
    currentlyDrawnEdge
  } = useDrawSkeleton({
    isDrawing,
    selectedVertexIdx,
    hoveredVertexIdx,
    drawingSkeleton,
    setDrawingSkeleton,
    templateConfigs: [{
      template: HUMAN_SKELETON_TEMPLATE,
      toolType: SKELETON_TOOLS.HUMAN_SKELETON_TOOL
    }]
  });
  useUpdateSkeleton({
    annotation,
    annotationIdx,
    selectedVertexIdx,
    disableChanges,
    skeletonRef,
    setDrawingSkeleton,
    setSelectedVertexIdx
  });
  useEffect(() => {
    setDrawingSkeleton(annotation ?? null);
    if (annotation) {
      const points = annotation.data.nodes.map(node => node.point);
      const bbox = calculateBoundingBox(points);
      setBoundingBox(bbox);
    }
  }, [annotation]);
  useEffect(() => {
    if (skeletonRef.current && !isDrawing && drawingSkeleton) {
      const box = skeletonRef.current.getBBox();
      setLabelCoords([box.x + box.width / 2, box.y + box.height / 2]);
      const points = drawingSkeleton.data.nodes.map(node => node.point);
      const bbox = calculateBoundingBox(points);
      setBoundingBox(bbox);
    }
  }, [isDrawing, drawingSkeleton]);
  const handleScaleHandleSelect = (vertex: {
    type: (typeof VERTEX_TYPE)[keyof typeof VERTEX_TYPE];
  }) => {
    if (isScaleVertexType(vertex.type)) {
      setSelectedScaleHandle(vertex.type);
      if (boundingBox) {
        originalBoundingBoxRef.current = {
          ...boundingBox
        };
        anchorPointRef.current = getAnchorPointForVertex(boundingBox, vertex);
      }
    }
  };
  const handleScaleHandleDeselect = () => {
    setSelectedScaleHandle(null);
    originalBoundingBoxRef.current = null;
    anchorPointRef.current = null;
  };
  const handleSkeletonUpdate = (scaledData: SkeletonDataT) => {
    if (drawingSkeleton) {
      setDrawingSkeleton({
        ...drawingSkeleton,
        data: scaledData
      });
    }
  };
  const handleScaleComplete = (scaledData: SkeletonDataT) => {
    if (annotationIdx && drawingSkeleton) {
      updateAnnotation(annotationIdx, {
        ...drawingSkeleton,
        data: scaledData
      });
    }
    setSelectedScaleHandle(null);
    originalBoundingBoxRef.current = null;
    anchorPointRef.current = null;
  };

  // eslint-disable-next-line react-hooks/refs
  useShapeScaling({
    isDrawing: !!isDrawing,
    isEditable,
    isLocked,
    selectedScaleHandle,
    // eslint-disable-next-line react-hooks/refs
    originalBoundingBox: originalBoundingBoxRef.current,
    currentData: drawingSkeleton?.data || null,
    annotationIdx,
    applyScaleFunction: (data, transform) => anchorPointRef.current ? scaleSkeletonData(data, transform, anchorPointRef.current) : data,
    onDataUpdate: handleSkeletonUpdate,
    onScaleComplete: handleScaleComplete
  });
  const skeletonStyle = useMemo(() => getAnnotationStyle({
    fill: drawingSkeleton?.annotationLabel.style.fillColor,
    fillOpacity: 0,
    stroke: drawingSkeleton?.annotationLabel.style.strokeColor,
    ...getStylesForStylingType(stylingType)
  }, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    overrideOpacity: true,
    isM4: isVideoLabelingM4Enabled
  }), [drawingSkeleton?.annotationLabel.style.fillColor, drawingSkeleton?.annotationLabel.style.strokeColor, isFocused, isLocked, stylingType, isVideoLabelingM4Enabled]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!drawingSkeleton) {
    const stageCenter = getStageCenter();
    return <g ref={skeletonRef} points={`${stageCenter[0]},${stageCenter[1]}`} style={skeletonStyle}></g>;
  }
  const nodes = drawingSkeleton.data.nodes;
  const edges = drawingSkeleton.data.edges;
  return <>
      <Shape vertices={nodes.map(node => ({
      point: node.point,
      type: VERTEX_TYPE.MOVE,
      color: node.color
    }))} isLocked={isLocked} annotationLabel={{
      ...drawingSkeleton.annotationLabel,
      labelCoords
    }} isDrawing={isDrawing} shouldShowVertexDuringDrawing={isDrawing} annotationIdx={annotationIdx} metadataPillText={dynamicAttributesText} onVertexSelect={(_, index) => setSelectedVertexIdx(index)} onVertexEnter={(_, index) => setHoveredVertexIdx(index)} onVertexLeave={() => setHoveredVertexIdx(null)} dataAttributes={{
      'data-e2e-id': `annotation-${drawingSkeleton.annotationLabel.title}-${drawingSkeleton.annotationType}`,
      'data-annotation-id': annotationIdx?.toString() || ''
    }}>
        <g ref={skeletonRef} data-testId={'skeleton-container'}>
          {currentlyDrawnEdge && <line style={skeletonStyle} x1={currentlyDrawnEdge[0][0]} y1={currentlyDrawnEdge[0][1]} x2={currentlyDrawnEdge[1][0]} y2={currentlyDrawnEdge[1][1]} />}
          {edges.map(edge => {
          const startPoint = nodes.find(node => node.id === edge.startNode)?.point;
          const endPoint = nodes.find(node => node.id === edge.endNode)?.point;
          if (!startPoint || !endPoint) {
            return <></>;
          }
          return <line key={`${edge.startNode}-${edge.endNode}`} style={skeletonStyle} x1={startPoint[0]} y1={startPoint[1]} x2={endPoint[0]} y2={endPoint[1]} />;
        })}
          {(isFocused || isEditable) && !disableChanges && <NodeLabels nodes={nodes} activeIndex={hoveredVertexIdx ?? selectedVertexIdx} outlined />}
        </g>
      </Shape>
      {!isDrawing && isEditable && !isLocked && boundingBox && <ScalingBoundingBox boundingBox={boundingBox} isVisible={true} strokeColor={drawingSkeleton.annotationLabel.style.strokeColor} onScaleHandleSelect={handleScaleHandleSelect} onScaleHandleDeselect={handleScaleHandleDeselect} />}
    </>;
};